import { computed, inject, Injectable, makeStateKey, Signal, signal, TransferState, WritableSignal } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { GsDropdownMenuItem } from '../../controllers';
import { ConfigModel, ICurrency } from '../../models';
import { IInit, IInitMenu, MenuType } from '../../models/structure/init';
import { CmsProvider } from '../../providers';
import { LocalStorageService } from '../localstorage.service';
import { SystemService } from '../system.service';
import { iconNameFromFontAwesome } from '../../utils/fontawesome-icon.util';

/** LocalStorage key holding the cached init data */
const INIT_DATA_KEY = 'init_data';
/** LocalStorage key holding the timestamp (ms) of the last successful init fetch */
const INIT_LAST_LOAD_KEY = 'init_last_backend_load';
/** TransferState key of the init data the server rendered with */
const INIT_STATE_KEY = makeStateKey<IInit>('init');
/** Fallback polling interval in minutes when no config value is available */
const DEFAULT_INIT_POLLING_INTERVAL = 60;

@Injectable({
  providedIn: 'root'
})
export class InformationService {
  // Services
  private readonly cmsService = inject(CmsProvider);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly systemService = inject(SystemService);
  private readonly transferState = inject(TransferState);

  // Properties & Signals
  /** The init-data coming from the backend */
  public init: WritableSignal<IInit | undefined> = signal(undefined);
  /** All currencies listed by the backend */
  currencies: Signal<ICurrency[]> = computed(() => {
    return this.init()?.currencies || [];
  });
  /** Whether a checkout page exists — gates cart/checkout UI. */
  readonly hasCheckout: Signal<boolean> = computed(() => this.init()?.hasCheckout ?? false);
  /** Whether an inquiry checkout page exists — gates the Anfrageliste UI. */
  readonly hasInquiry: Signal<boolean> = computed(() => this.init()?.hasInquiry ?? false);

  // Methods
  /**
   * Provides the init data, served from the localStorage cache while fresh and only re-fetched from
   * the backend once the configured polling interval elapsed. Updates the `init` signal and the cache.
   * @returns an observable emitting the init data (cached or freshly fetched)
   */
  initialize(): Observable<IInit> {
    // The browser starts with the init data the server rendered with, so hydration finds the same menus
    const serverInit = this.transferState.get(INIT_STATE_KEY, null);
    if (serverInit && this.systemService.browser) {
      this.transferState.remove(INIT_STATE_KEY);
      this.cacheInit(serverInit);
      return of(serverInit);
    }

    // Hydrate the signal from the cache once so consumers have data immediately (also offline)
    if (!this.init()) {
      const cached = this.localStorageService.get(INIT_DATA_KEY) as IInit | null;
      if (cached) this.init.set(cached);
    }

    const current = this.init();
    if (current && !this.isStale()) {
      this.systemService.log('[InformationService] Using cached init data, skipping backend reload');
      return of(current);
    }

    // Fetch fresh data and update the signal + cache only when data is available
    return this.cmsService.init().pipe(
      tap({
        next: (res) => {
          this.cacheInit(res);
          if (!this.systemService.browser) this.transferState.set(INIT_STATE_KEY, res);
          this.systemService.log('[InformationService] Init data fetched successfully:', this.init());
        },
        error: (err) => {
          // Keep any cached data on failure instead of blanking the signal
          console.error('[InformationService] Failed to fetch init data:', err.message);
        }
      })
    );
  }

  /**
   * Sets the init data and stores it in the localStorage cache, stamped as freshly loaded.
   * @param init The init data loaded from the backend or rendered with on the server
   */
  private cacheInit(init: IInit): void {
    this.init.set(init);
    this.localStorageService.set(INIT_DATA_KEY, init);
    this.localStorageService.set(INIT_LAST_LOAD_KEY, Date.now());
  }

  /**
   * Clears the cached init data (localStorage and the in-memory signal). Call this when switching
   * apps/tenants so stale shop data (company info, menus, methods) is not served for the new app.
   */
  clearCache(): void {
    this.localStorageService.remove(INIT_DATA_KEY);
    this.localStorageService.remove(INIT_LAST_LOAD_KEY);
    this.init.set(undefined);
  }

  /**
   * Returns the currency sign or nameShort for the provided currency ID.
   * @param iconType Optional. The type of currency display to return. Can be `'sign'` or `'nameShort'`.
   * @param currencyId The ID of the currency.
   * @deprecated Use the Pipe 'formatCurrency' instead.
   * @returns The currency sign if the currency is found, otherwise an empty string.
   */
  toCurrencySign(iconType: 'sign' | 'nameShort' = 'sign', currencyId?: number): string {
    const currenciesAvailable = this.currencies();
    if (currenciesAvailable.length === 0) return '';
    const currency = currenciesAvailable.find((c) => c.iD === currencyId) ?? currenciesAvailable[0];
    if (!currency) return '';
    return iconType === 'nameShort' ? currency.nameShort : (currency.sign ?? currency.nameShort);
  }

  /**
   * Generates a dropdown menu structure from the provided backend menu data.
   * This recursively processes nodes to build a hierarchy of menu items.
   *
   * @param backendMenus The list of menus received from the backend CMS.
   * @returns An array of GsDropdownMenuItem representing the dropdown menu items.
   */
  generateMenu(backendMenus?: IInitMenu[]): GsDropdownMenuItem[] {
    if (!backendMenus) return [];
    return backendMenus.map((menu) => {
      return {
        label: menu.name ?? '',
        items: menu.nodes?.length > 0 ? this.generateMenu(menu.nodes) : [],
        linkUrl: menu.url,
        linkTarget: '_self',
        iD: menu.iD,
        opened: false,
        menuType: menu.menuItemType ?? MenuType.Dropdown,
        containerItems: menu.containerItems?.length > 0 ? menu.containerItems : [],
        glyph: iconNameFromFontAwesome(menu.glyph),
        photo: menu.photo
      };
    });
  }

  /**
   * Whether the cached init data is stale and must be re-fetched, based on the polling interval and
   * the last-fetch timestamp. Presence of cached data is guarded by the caller, not here.
   * @returns true if the interval elapsed (or no valid timestamp exists / caching is disabled)
   */
  private isStale(): boolean {
    if (!this.systemService.browser) return true;

    // Cheap timestamp check first — avoids parsing the config JSON when we have to reload anyway
    const lastLoad = this.parseTimestamp(this.localStorageService.get(INIT_LAST_LOAD_KEY));
    if (lastLoad === null) return true;

    const interval = this.resolveInterval();
    // 0 disables caching → always reload
    if (interval === 0) return true;

    return Date.now() - lastLoad >= interval * 60 * 1000;
  }

  /**
   * Reads the init polling interval (minutes) from the persisted config, falling back to the default.
   * Read directly from localStorage because InformationService initializes before ConfigService
   * has populated its config signal.
   * @returns the interval in minutes
   */
  private resolveInterval(): number {
    const config = this.localStorageService.get('config') as ConfigModel | null;
    const interval = config?.initPollingInterval;
    // Guard against corrupted/non-numeric values that would make isStale() compute NaN (never stale)
    return typeof interval === 'number' && Number.isFinite(interval) && interval >= 0 ? interval : DEFAULT_INIT_POLLING_INTERVAL;
  }

  /**
   * Parses a raw localStorage value into a positive epoch-millisecond timestamp.
   * @param raw the stored value (number or numeric string)
   * @returns the timestamp in ms, or null when invalid
   */
  private parseTimestamp(raw: unknown): number | null {
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
    if (typeof raw === 'string') {
      const parsed = Number(raw);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return null;
  }
}
